import React, { useState } from 'react';
import { X, UsersThree, Sparkle, Check } from '@phosphor-icons/react';
import { flattenSlotForRating } from './utils';

// In multi-eater mode plan.breakfast/lunch/dinner/snacks can be a dict
// (e.g. {by_profile: {...}} or {primary, variations}). Raw `.trim()` on those
// crashed prod with "(t[e] || '').trim is not a function". Always flatten first.
const slotStr = (v) => flattenSlotForRating(v).trim();

export default function ServingsBadge({ plan, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(plan.servings || '');
  const current = plan.servings || null;
  const save = () => {
    const n = parseInt(val, 10);
    if (!isNaN(n) && n >= 1 && n <= 50) {
      onUpdate(plan.plan_id, n);
    }
    setEditing(false);
  };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px]"
        style={{ background: 'rgba(168,85,247,0.10)', border: '1px solid rgba(168,85,247,0.30)' }}>
        <UsersThree size={9} weight="bold" className="text-purple-300" />
        <input type="number" min="1" max="50" value={val} onChange={e => setVal(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          autoFocus data-testid={`servings-input-${plan.plan_id}`}
          className="bg-transparent outline-none w-10 text-purple-200 font-bold text-[10px]" />
        <button onClick={save} className="text-emerald-300" data-testid={`servings-save-${plan.plan_id}`}><Check size={9} weight="bold" /></button>
        <button onClick={() => setEditing(false)} className="text-neutral-400"><X size={9} weight="bold" /></button>
      </span>
    );
  }
  return (
    <button onClick={() => setEditing(true)} data-testid={`servings-badge-${plan.plan_id}`}
      title="Servings for this day (overrides default). Tap to change."
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold hover:bg-purple-500/20 transition-colors"
      style={{ background: 'rgba(168,85,247,0.10)', border: '1px solid rgba(168,85,247,0.25)', color: '#D8B4FE' }}>
      <UsersThree size={9} weight="bold" />
      Serves {current || 'default'}
    </button>
  );
}

export function StaffMenuStrip({ plan, api, onUpdate }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [savingOverride, setSavingOverride] = useState(false);

  const slots = ['breakfast', 'lunch', 'dinner', 'snacks'];
  const hasAnyStaff = slots.some(s => slotStr(plan[`staff_${s}`]));
  const sharedSlots = slots.filter(s => plan[`staff_shares_${s}`] && slotStr(plan[s]));
  const overrideMap = plan.staff_share_override || {};

  const generate = async () => {
    setBusy(true); setErr('');
    try {
      await api.post(`/meals/plans/${plan.plan_id}/generate-staff-menu`, {});
      onUpdate && onUpdate();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to generate.');
    }
    setBusy(false);
  };

  const setOverride = async (slot, mode) => {
    // mode: 'share' | 'separate' | null (= auto)
    setSavingOverride(true);
    try {
      const next = { ...overrideMap };
      if (mode) next[slot] = mode;
      else delete next[slot];
      await api.patch(`/meals/plans/${plan.plan_id}`, { staff_share_override: next });
      // Re-run staff generation to honour the new override.
      await api.post(`/meals/plans/${plan.plan_id}/generate-staff-menu`, {});
      onUpdate && onUpdate();
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Override failed.');
    }
    setSavingOverride(false);
  };

  const clearStaff = async () => {
    if (!window.confirm('Clear the staff menu for this day?')) return;
    try {
      await api.patch(`/meals/plans/${plan.plan_id}`, {
        staff_breakfast: '', staff_lunch: '', staff_dinner: '', staff_snacks: '',
        staff_shares_breakfast: false, staff_shares_lunch: false,
        staff_shares_dinner: false, staff_shares_snacks: false,
      });
      onUpdate && onUpdate();
    } catch (e) {
      window.alert(e?.response?.data?.detail || 'Clear failed.');
    }
  };

  const SLOT_LABEL = { breakfast: 'B', lunch: 'L', dinner: 'D', snacks: 'S' };

  const renderSlotRow = (s, kind) => {
    // kind: 'shared' | 'separate' — drives the body label
    const family = slotStr(plan[s]);
    const dishes = slotStr(plan[`staff_${s}`]);
    const ov = overrideMap[s];
    return (
      <div key={s} className="flex items-start gap-2 text-[10px] py-0.5"
        data-testid={`staff-slot-row-${s}-${plan.plan_id}`}>
        <span className={`font-bold w-3 flex-shrink-0 mt-0.5 ${kind === 'shared' ? 'text-emerald-300' : 'text-purple-300'}`}>{SLOT_LABEL[s]}</span>
        <div className="flex-1 min-w-0">
          {kind === 'shared'
            ? <span className="text-emerald-300/80 italic">🍽️ Sharing — eats {family}</span>
            : <span className="text-neutral-300">{dishes}</span>}
          <div className="flex gap-0.5 mt-0.5"
            data-testid={`staff-override-${s}-${plan.plan_id}`}>
            {[
              { id: null, label: 'Auto' },
              { id: 'share', label: 'Share' },
              { id: 'separate', label: 'Separate' },
            ].map(opt => {
              const active = (ov || null) === opt.id;
              return (
                <button key={opt.label}
                  onClick={() => setOverride(s, opt.id)}
                  disabled={savingOverride}
                  data-testid={`staff-override-${s}-${opt.id || 'auto'}-${plan.plan_id}`}
                  className={`px-1.5 py-0.5 rounded text-[9px] font-bold transition ${active
                    ? 'bg-purple-500/30 text-purple-200 ring-1 ring-purple-400/50'
                    : 'bg-white/5 text-neutral-500 hover:bg-white/10 hover:text-neutral-300'}`}>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="mt-3 rounded-xl p-2.5" data-testid={`staff-strip-${plan.plan_id}`}
      style={{ background: 'rgba(139,92,246,0.06)', border: '1px solid rgba(139,92,246,0.20)' }}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <div className="flex items-center gap-1.5">
          <UsersThree size={12} weight="bold" className="text-purple-300" />
          <span className="text-[10px] font-bold uppercase tracking-wider text-purple-300">Staff menu</span>
          {plan.staff_sharing_mode_used && (
            <span className="text-[9px] font-bold uppercase tracking-wider text-neutral-500" data-testid={`staff-mode-used-${plan.plan_id}`}>
              · {plan.staff_sharing_mode_used.replace(/_/g, ' ')}
            </span>
          )}
        </div>
        <div className="flex gap-1">
          <button onClick={generate} disabled={busy || savingOverride}
            data-testid={`staff-gen-${plan.plan_id}`}
            className="px-2 py-0.5 rounded-md text-[10px] font-bold bg-purple-500/20 text-purple-200 border border-purple-500/40 hover:bg-purple-500/30 disabled:opacity-50 flex items-center gap-1">
            {busy ? (
              <>
                <div className="w-2.5 h-2.5 border border-purple-300/40 border-t-purple-200 rounded-full animate-spin" />
                AI…
              </>
            ) : (
              <>
                <Sparkle size={9} weight="fill" /> {hasAnyStaff || sharedSlots.length > 0 ? 'Regen' : 'Generate'}
              </>
            )}
          </button>
          {(hasAnyStaff || sharedSlots.length > 0) && (
            <button onClick={clearStaff} data-testid={`staff-clear-${plan.plan_id}`}
              className="px-1.5 py-0.5 rounded-md text-neutral-500 hover:text-red-300" title="Clear staff menu">
              <X size={10} weight="bold" />
            </button>
          )}
        </div>
      </div>
      {sharedSlots.length === 0 && !hasAnyStaff ? (
        <p className="text-[10px] text-neutral-500 italic">
          {err || 'Cooking staff often eats simpler food. Generate a 1-dal + 1-sabzi version with AI.'}
        </p>
      ) : (
        <div className="space-y-0.5">
          {sharedSlots.map(s => renderSlotRow(s, 'shared'))}
          {slots.map(s => {
            if (!slotStr(plan[`staff_${s}`])) return null;
            return renderSlotRow(s, 'separate');
          })}
        </div>
      )}
    </div>
  );
}
